import { Box, Text, IconButton } from 'theme-ui';

import CloseIcon from '~/assets/images/icons/close.svg';
import ImgLoader from '~/components/Generic/ImgLoader';
import AnimatedModal from '~/components/Generic/Modal/AnimatedModal';

type Props = {
  isOpen: boolean;
  onDismiss: () => void;
  sizeChart: Queries.SizeChartFragment | null;
};

const SizeChart = ({ isOpen, onDismiss, sizeChart }: Props) => {
  if (!sizeChart) return null;
  const { measurements, image, title, category, intro, details } = sizeChart;
  const measurementOptions =
    measurements?.reduce<{ [key: string]: { label: string | null; value: string | null }[] }>((acc, measurement) => {
      if (!measurement) return acc;
      const { title, sizeRange, sizeReference } = measurement;
      if (!title || !sizeRange || !sizeReference) return acc;
      return { ...acc, [title]: [...(acc[title] || []), { label: sizeReference?.presentation, value: sizeRange }] };
    }, {}) ?? {};

  const labels = new Set(
    Object.values(measurementOptions)
      .map((mo) => mo.map((o) => o.label))
      .flat()
  );

  return (
    <AnimatedModal
      isOpen={isOpen}
      onDismiss={onDismiss}
      ariaLabel="Size Chart"
      forwardSx={{
        mx: '2.4rem',
        my: '6.4rem',
        maxHeight: 'calc(100vh - 12.8rem)',
        overflow: 'auto',
      }}
      allowPinchZoom
    >
      <Box
        sx={{
          position: 'relative',
          width: '100%',
          padding: ['3rem', '4rem'],
          fontSize: ['1.1rem', '1.3rem'],
          minWidth: ['85vw', 'auto'],
          maxWidth: ['min-content', '90rem'],
          lineHeight: [1.3, 1.2],
        }}
      >
        <IconButton
          onClick={onDismiss}
          variant="bare"
          sx={{
            position: 'absolute',
            top: ['3rem', '4rem'],
            right: ['2.4rem', '4rem'],
            height: ['1.6rem', '4rem'],
            width: ['1.6rem', '4rem'],
            svg: {
              height: '100%',
              width: '100%',
              maxHeight: ['1.6rem', '4rem'],
              maxWidth: ['1.6rem', '4rem'],
            },
          }}
        >
          <CloseIcon />
        </IconButton>

        <Box as="header">
          <Text as="h3" variant="h4" sx={{ mb: ['0.6rem', '1rem'], fontSize: ['1.1rem', '1.8rem'] }}>
            {title}
          </Text>
          <Text as="h4" variant="h3" sx={{ mb: '2.4rem', fontSize: ['2.3rem', '3.4rem'] }}>
            {category}
          </Text>
        </Box>
        {intro?.childMarkdownRemark?.html && (
          <Box
            sx={{ lineHeight: 1.6, mt: 0, mb: ['2.4rem', '1.4rem'], p: { m: 0 } }}
            dangerouslySetInnerHTML={{ __html: intro?.childMarkdownRemark?.html }}
          />
        )}
        {image ? (
          <ImgLoader image={image} alt={title} forwardSx={{ my: ['1.6rem', '2.4rem'] }} objectFit="contain" />
        ) : (
          <Box
            as="section"
            sx={{
              mb: ['2.4rem', '3.2rem'],
              overflowX: 'auto',
              table: {
                width: '100%',
                textAlign: 'center',
                lineHeight: '100%',
                borderCollapse: 'collapse',
                position: 'relative',
                'th, td': {
                  py: ['0.925rem', '1.275rem'],
                  whiteSpace: 'nowrap',
                },
                'td:first-of-type, th:first-of-type': {
                  textAlign: 'left',
                  position: 'sticky',
                  left: 0,
                  zIndex: 1,
                  background: 'var(--backgroundcolor)',
                  pr: '2.4rem',
                },
                'td:not(:first-of-type), th:not(:first-of-type)': { px: '2.4rem' },
                'th, tr:not(:last-of-type) td': {
                  borderBottom: '1px solid',
                  borderBottomColor: 'translucentOlive',
                },
                'th + th:not(:nth-of-type(2)), td + td:not(:nth-of-type(2))': {
                  borderLeft: '1px solid',
                  borderLeftColor: 'translucentOlive',
                },
              },
            }}
          >
            <article>
              <table>
                <thead>
                  <tr>
                    <th></th>
                    {labels && Array.from(labels).map((label) => <th key={label}>{label}</th>)}
                  </tr>
                </thead>

                <tbody>
                  {measurementOptions &&
                    Object.keys(measurementOptions).length &&
                    Object.keys(measurementOptions).map((k) => (
                      <tr key={k}>
                        <td>{k}</td>
                        {measurementOptions[k]?.map((v, i) =>
                          v ? <td key={`${k}-${i}`}>{v.value}</td> : <td key={`${k}-${i}`}>-</td>
                        )}
                      </tr>
                    ))}
                </tbody>
              </table>
            </article>
          </Box>
        )}

        {details?.childMarkdownRemark?.html && (
          <Box
            sx={{
              h3: {
                mt: 0,
                mb: ['1.8rem', '1.6rem'],
              },

              p: {
                my: 0,
              },
              ul: {
                pl: ['2.4rem', '3.2rem'],
                my: 0,
              },
              'ul:not(:last-of-type), p:not(:last-of-type)': {
                mb: ['2.4rem', '3.2rem'],
              },
              'li:not(:last-of-type)': {
                mb: ['1.4rem', '1.6rem'],
              },
            }}
            dangerouslySetInnerHTML={{ __html: details?.childMarkdownRemark?.html }}
          />
        )}
      </Box>
    </AnimatedModal>
  );
};

export default SizeChart;
